export const siteUrl = 'https://takkasmart.com';
export const eatsUrl = 'https://eats.takkasmart.com/';
export const verifiedSupportEmail = 'takkasmart@gmail.com';
export const verifiedSupportPhone = '01016420204';
export const verifiedSupportPhoneInternational = '+201016420204';

export const services = [
  {
    slug: 'digital-business-card',
    eyebrow: 'Share a complete profile',
    title: 'Digital Business Cards',
    shortTitle: 'Digital Cards',
    summary: 'A mobile-ready business profile that keeps contact details, social channels, location, and useful links in one clear destination.',
    benefit: 'Replace scattered contact details with one profile you can keep current.',
    audience: 'Consultants, sales teams, founders, creators, clinics, and customer-facing professionals.',
    outcomes: [
      'Share contact and business information from one link.',
      'Keep the destination useful after the first introduction.',
      'Present social links, website, location, and contact actions clearly.',
      'Update permitted profile information through the existing client account.'
    ],
    useCases: [
      'Professional networking and events',
      'Sales representatives and field teams',
      'Personal brands and service providers',
      'Profiles connected to printed QR materials'
    ],
    howItWorks: [
      'Takka Smart prepares the digital profile for the selected solution.',
      'The profile is connected to a direct link, QR code, NFC product, or a combination of them.',
      'The client signs in with the account issued for the profile.',
      'Permitted details can be updated without changing the public profile link.'
    ],
    faqs: [
      ['Can I update my information later?', 'Yes. Existing clients can sign in and update the profile fields available to their account without replacing the public profile link.'],
      ['Does the person opening my card need an app?', 'No dedicated Takka Smart app is required to open a standard web profile. The visitor opens it in a mobile browser.'],
      ['Can the same profile be used with QR and NFC?', 'Where the physical product is prepared with both technologies, the same destination can be opened by tapping NFC or scanning QR.']
    ]
  },
  {
    slug: 'nfc-business-card',
    eyebrow: 'Tap to introduce your business',
    title: 'NFC Business Cards',
    shortTitle: 'NFC Cards',
    summary: 'A branded physical card that opens a digital destination when tapped on a compatible phone, with QR available as a practical companion.',
    benefit: 'Give in-person introductions a faster path to useful digital information.',
    audience: 'Teams and professionals who meet customers, partners, or prospects face to face.',
    outcomes: [
      'Connect a physical card to a digital profile or chosen destination.',
      'Reduce dependence on reprinting when digital details change.',
      'Support both NFC tap and QR scan workflows where configured.',
      'Customize the printed appearance around the business identity.'
    ],
    useCases: [
      'Client meetings and exhibitions',
      'Property, automotive, medical, and hospitality teams',
      'Business development and recruitment',
      'Branded staff cards'
    ],
    howItWorks: [
      'Choose the card use case and provide the approved brand material.',
      'Takka Smart prepares the card design and digital destination.',
      'The NFC chip and QR destination are configured for the approved experience.',
      'The recipient taps or scans and opens the information on their phone.'
    ],
    faqs: [
      ['What does NFC mean?', 'Near Field Communication is a short-range technology that lets a compatible phone read a prepared NFC tag when placed close to it.'],
      ['What if a phone does not support NFC?', 'A printed QR code can provide a second access path when it is included in the product design.'],
      ['Will NFC take a payment by itself?', 'No. These products open the configured digital destination; any payment action is handled by the destination or provider selected by the client.']
    ]
  },
  {
    slug: 'smart-stands',
    eyebrow: 'A physical touchpoint with a digital destination',
    title: 'NFC & QR Smart Stands',
    shortTitle: 'Smart Stands',
    summary: 'Custom-branded acrylic stands that guide customers to menus, payment links, reviews, social channels, profiles, or another approved web destination.',
    benefit: 'Turn a counter, table, reception, or checkout point into an easy digital interaction.',
    audience: 'Restaurants, cafés, clinics, salons, stores, offices, events, and service counters.',
    outcomes: [
      'Use NFC and QR together for a flexible customer journey.',
      'Match the printed stand to the business brand.',
      'Select a focused experience such as menu, payment, review, or social profile.',
      'Place the interaction where customers already make decisions.'
    ],
    useCases: [
      'Digital menus on restaurant tables',
      'Payment-provider links at a cashier',
      'Google review requests after service',
      'Social and contact links at reception',
      'Price lists, catalogs, and campaign pages'
    ],
    howItWorks: [
      'Define the action customers should take after a tap or scan.',
      'Provide brand assets and approve the printed design.',
      'Takka Smart prepares the acrylic stand and configured destination.',
      'Place the stand at the point where the interaction is most useful.',
      'Update the digital destination when the selected solution supports account management.'
    ],
    faqs: [
      ['What can a smart stand open?', 'Depending on the selected setup, it can open a business profile, restaurant menu, payment-provider link, review page, social links, price list, or another approved web destination.'],
      ['Does the stand need electricity?', 'The QR code does not. Passive NFC tags used for tap interactions do not normally require a battery or power connection.'],
      ['Can the design match my brand?', 'Yes. Smart stands can be prepared with approved logos, colors, wording, and use-case instructions.']
    ]
  },
  {
    slug: 'qr-solutions',
    eyebrow: 'A familiar scan path for every customer',
    title: 'QR Solutions',
    shortTitle: 'QR Solutions',
    summary: 'Clear, branded QR touchpoints for profiles, menus, catalogs, contact destinations, reviews, and other approved business links.',
    benefit: 'Offer a camera-based access path that works alongside NFC or on its own.',
    audience: 'Any organization that needs a visible, scan-first bridge between printed material and online information.',
    outcomes: [
      'Connect print and signage to a useful mobile destination.',
      'Use clear calls to action so customers know what a scan will open.',
      'Add QR to stands, cards, signs, and branded printed material.',
      'Pair QR with NFC for broader device compatibility.'
    ],
    useCases: [
      'Restaurant menus and hospitality information',
      'Business profiles and contact sharing',
      'Reviews, feedback, and social destinations',
      'Payment-provider and price-list links'
    ],
    howItWorks: [
      'Confirm the destination and the purpose of the QR interaction.',
      'Create or validate the QR code for that destination.',
      'Place it in an approved layout with a descriptive call to action.',
      'Test the final printed output on multiple phone cameras before delivery.'
    ],
    faqs: [
      ['What happens after a customer scans?', 'Their phone opens the destination encoded in the QR code, usually after the camera shows a link confirmation.'],
      ['Should a QR code explain what it opens?', 'Yes. A short instruction such as “Scan to view the menu” makes the interaction clearer and more trustworthy.'],
      ['Can QR work without NFC?', 'Yes. QR is an independent camera-based access method and can be used alone.']
    ]
  },
  {
    slug: 'branding',
    eyebrow: 'Make the technology feel like your business',
    title: 'Branding & Smart Product Design',
    shortTitle: 'Branding',
    summary: 'Design support that connects a business identity with its physical smart product and the digital experience customers open.',
    benefit: 'Create a consistent journey from the printed touchpoint to the mobile screen.',
    audience: 'Businesses that need their smart cards, stands, and digital presentation to feel intentional and recognizable.',
    outcomes: [
      'Adapt approved logos, colors, and messages to smart products.',
      'Create clear tap and scan instructions.',
      'Coordinate physical product artwork with its digital destination.',
      'Prepare presentation assets for customer-facing use.'
    ],
    useCases: [
      'Branded smart stand artwork',
      'Business card and profile presentation',
      'QR signage and counter communication',
      'Restaurant menu touchpoints'
    ],
    howItWorks: [
      'Collect the approved logo, brand colors, and required wording.',
      'Clarify the purpose and viewing distance of the physical product.',
      'Prepare a design that keeps the action and QR code easy to understand.',
      'Review the artwork before production.',
      'Connect the final product to the tested digital destination.'
    ],
    faqs: [
      ['Do I need an existing brand guide?', 'No, but approved logo files, preferred colors, and examples help the design stay consistent.'],
      ['Can every design be printed exactly as shown on screen?', 'Print and screen colors can differ. Final production artwork may need practical adjustments for contrast, QR readability, and material.'],
      ['Do you create fake reviews or client logos for examples?', 'No. Portfolio work should use real supplied material and approved examples only.']
    ]
  },
  {
    slug: 'digital-restaurant-menu',
    eyebrow: 'Takka Eats',
    title: 'Digital Restaurant Menus',
    shortTitle: 'Restaurant Menus',
    summary: 'A dedicated menu experience for restaurants and hospitality businesses, available through Takka Eats and connected to QR or NFC touchpoints.',
    benefit: 'Give guests a phone-friendly menu that the restaurant can organize and update from its own system.',
    audience: 'Restaurants, cafés, bakeries, food brands, hotels, and hospitality venues.',
    outcomes: [
      'Organize products and categories for a mobile menu.',
      'Connect menu access to QR and suitable NFC products.',
      'Update menu information from the restaurant system.',
      'Give customers a clear browsing experience from their phones.'
    ],
    useCases: [
      'Table and counter menus',
      'Café and bakery product catalogs',
      'Hotel and hospitality dining information',
      'Menu links shared through social channels'
    ],
    howItWorks: [
      'The restaurant creates and organizes its menu in the Takka Eats system.',
      'Categories, products, details, and availability are prepared for customers.',
      'A menu link is connected to QR or an appropriate NFC touchpoint.',
      'Guests open the menu from their phones.',
      'The restaurant manages future menu updates in Takka Eats.'
    ],
    external: true,
    faqs: [
      ['Where do restaurant owners manage their menu?', 'Restaurant-specific menu management is handled through the separate Takka Eats platform.'],
      ['Will this website change existing restaurant menu links?', 'No. The corporate website only introduces Takka Eats and links to it; existing menu pages, accounts, data, and links remain separate and unchanged.'],
      ['Can guests view a menu without installing an app?', 'The public menu experience is designed to open from a shared link, QR code, or configured NFC touchpoint in the customer’s browser.']
    ]
  }
];

export const homeFaqs = [
  ['What is an NFC smart stand?', 'It is a physical, branded stand containing a configured NFC tag and usually a QR code. A customer taps or scans to open the intended digital destination.'],
  ['Does NFC require an application?', 'Opening a standard web destination does not require a dedicated Takka Smart app. A compatible phone can read the tag and open the link in a browser.'],
  ['Can I update my business information later?', 'Existing customers can sign in and update the information their current profile plan allows while keeping the same public profile link.'],
  ['What happens when someone scans the QR code?', 'The phone camera reads the code and offers to open the configured profile, menu, payment-provider page, review destination, or other approved link.'],
  ['Can the same profile work with QR and NFC?', 'Yes, when both are configured to open the same profile destination.'],
  ['What is a digital business card?', 'It is a web-based profile that gathers useful contact, business, and link information in one mobile-friendly page.'],
  ['Is the service suitable for restaurants?', 'Yes. Restaurant menus are provided through Takka Eats, while smart stands can make menu access clear at tables and counters.'],
  ['Where can restaurant owners manage digital menus?', 'Restaurant owners use the separate Takka Eats platform at eats.takkasmart.com.'],
  ['Can existing customers still access their accounts?', 'Yes. Client login and the existing protected profile-management routes remain available.'],
  ['Can Takka Smart customize the design for my brand?', 'Smart products can use approved brand assets, colors, and instructions appropriate to the product and destination.'],
  ['Does Takka Smart currently sell products online?', 'Not through this corporate website. Requests are handled through direct contact, and the public Shop experience is currently unavailable.'],
  ['How can I request a smart stand or digital menu?', 'Use the contact page for smart business solutions, or open Takka Eats for restaurant-menu services.']
];

export const guides = [
  {
    slug: 'what-is-an-nfc-business-card',
    title: 'What Is an NFC Business Card?',
    description: 'A practical explanation of NFC cards, what the recipient sees, and where QR fits into the experience.',
    readTime: '6 min read',
    sections: [
      ['The physical card and the digital destination', 'An NFC business card combines a physical branded object with a programmed digital destination. The card itself does not need to contain every phone number, title, social handle, or address. Instead, it can open a web profile that organizes those details for a mobile screen.'],
      ['What happens during a tap', 'When a compatible phone is held close to the NFC area, the phone reads the tag and presents the configured link. The recipient chooses to open it in the browser. The interaction is short-range and intentional; it is not a remote broadcast.'],
      ['Why QR is still useful', 'NFC is convenient, but QR gives users a familiar camera-based option. Including both methods can make a card more practical across different phones and customer preferences.'],
      ['What businesses should prepare', 'Before production, confirm the exact destination, approved logo, brand colors, name and role, contact actions, and the wording that tells the recipient to tap or scan. Test the final destination on mobile before printing.']
    ]
  },
  {
    slug: 'how-smart-stands-work',
    title: 'How Smart Stands Work',
    description: 'How an acrylic NFC and QR stand connects a physical customer moment to a menu, payment link, review, or profile.',
    readTime: '7 min read',
    sections: [
      ['Start with one clear action', 'A smart stand works best when it answers one immediate question: view the menu, open payment options, leave a review, follow the business, or see a price list. A focused instruction reduces uncertainty.'],
      ['NFC and QR are access methods', 'The stand is a visible physical prompt. NFC and QR are two ways to reach the same or related digital destination. Neither method replaces the destination itself, so the linked page must also be mobile-friendly and clear.'],
      ['Placement changes the result', 'A payment stand belongs near checkout, a menu stand on the table, and a review request after service. Good placement aligns the action with the moment when a customer is ready to take it.'],
      ['Keep the destination maintained', 'If the linked system allows updates, keep prices, links, contact details, and availability current. The physical stand can stay in place while the managed digital information changes.']
    ]
  },
  {
    slug: 'qr-vs-nfc-for-businesses',
    title: 'QR vs NFC for Businesses',
    description: 'Compare tap and scan experiences, compatibility, placement, and when using both is the better choice.',
    readTime: '8 min read',
    sections: [
      ['QR: visible and familiar', 'A QR code gives customers a visible target they can scan with a phone camera. It works well on menus, posters, packaging, counters, cards, and other printed material. Clear space and strong contrast are essential.'],
      ['NFC: close and direct', 'NFC provides a tap-style interaction on compatible phones. It is useful when the physical object can be brought close to the phone, such as a stand, card, or tag. The design should clearly mark the tap area.'],
      ['Why many businesses use both', 'Using NFC and QR together gives the customer a choice. NFC can feel immediate, while QR remains easy to recognize and works independently. Both should be tested against the exact final destination.'],
      ['Choose around the customer journey', 'The right method depends on viewing distance, lighting, staff guidance, phone access, and what happens after opening. Technology should simplify the step, not become the message itself.']
    ]
  },
  {
    slug: 'how-digital-restaurant-menus-work',
    title: 'How Digital Restaurant Menus Work',
    description: 'A restaurant-focused guide to menu organization, mobile access, QR and NFC placement, and ongoing updates.',
    readTime: '7 min read',
    sections: [
      ['A menu designed for a phone', 'A digital restaurant menu presents categories, products, descriptions, prices, and images in a format guests can browse on a mobile screen. Navigation and availability matter as much as visual design.'],
      ['Access at the right point', 'Restaurants can place a QR or NFC stand where guests naturally look for menu information: table, counter, reception, room, or takeaway area. The instruction should say clearly that the interaction opens the menu.'],
      ['Organize before decorating', 'Useful categories, accurate names, current prices, and sensible item order create the foundation. Images and branding support that structure rather than replace it.'],
      ['Manage changes in Takka Eats', 'Restaurant owners manage their restaurant-specific menu information in the separate Takka Eats platform. The corporate Takka Smart website only introduces and links to that service.']
    ]
  },
  {
    slug: 'how-to-update-your-digital-business-card',
    title: 'How to Update Your Digital Business Card',
    description: 'A safe checklist for existing Takka Smart customers before changing the information on a live profile.',
    readTime: '5 min read',
    sections: [
      ['Use the existing client login', 'Open Client Login from the Takka Smart website and sign in with the credentials already connected to the profile. Do not create a replacement account just to change an existing card.'],
      ['Review before saving', 'Check the spelling and destination of every phone number, email, website, map location, and social link. Open important links in a separate browser tab before saving them to a live profile.'],
      ['Keep the same public link', 'Normal profile edits do not require changing the public customer URL. That continuity is important because the existing link may already be printed in a QR code or written to an NFC product.'],
      ['Ask for support when access is unclear', 'If the profile, account, or allowed fields do not match the product you received, contact support instead of creating duplicate profiles or changing unrelated account settings.']
    ]
  }
];
